import { Link } from "react-router-dom";
import { ShieldCheck, Users, MapPin, BadgeCheck, Lock } from "lucide-react";

/**
 * TrustStrip — global trust bar rendered under the navbar on every public page.
 * Reinforces the 5,000+ verified supplier network + contact protection.
 * v2 (Feb 2026) — sharpened per new design system: Deep Navy surface,
 * Saffron kinetic anchor, IBM Plex Mono numerals, tinted-grey rule.
 * Compact on mobile (icon+number only), full on desktop.
 */

const ITEMS = [
  { icon: Users,       value: "5,000+", label: "Verified Suppliers",      to: "/suppliers-directory" },
  { icon: MapPin,      value: "100+",   label: "Cities Pan India",        to: "/categories" },
  { icon: BadgeCheck,  value: "KYC",    label: "Checked · GST on File",   to: "/become-supplier" },
  { icon: Lock,        value: "Lead",   label: "Protection Enabled",      to: "/post-requirement" },
  { icon: ShieldCheck, value: "EHI",    label: "Managed Communication",   to: "/contact" },
];

export default function TrustStrip() {
  return (
    <div
      className="bg-[#050B14] text-slate-200 border-b border-slate-800 hidden sm:block relative overflow-hidden"
      data-testid="trust-strip"
    >
      {/* Saffron kinetic hairline top-edge */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ehi-accent/70 to-transparent" aria-hidden />
      <div className="ds-page">
        <div className="flex items-center gap-5 lg:gap-8 overflow-x-auto no-scrollbar py-2.5">
          <span
            className="text-[10px] uppercase tracking-[0.22em] font-bold text-ehi-accent shrink-0"
            style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
          >
            Trust Network ·
          </span>
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <Link
                key={i}
                to={it.to}
                className="flex items-center gap-2 shrink-0 group focus:outline-none focus-visible:ring-1 focus-visible:ring-ehi-accent rounded-sm"
                data-testid={`trust-strip-item-${i}`}
              >
                <Icon className="w-3.5 h-3.5 text-ehi-accent group-hover:scale-110 transition-transform" strokeWidth={2.2} />
                <span className="text-xs font-bold text-white font-mono-tab tabular-nums">{it.value}</span>
                <span className="text-xs text-slate-400 group-hover:text-white transition-colors uppercase tracking-wider">
                  {it.label}
                </span>
                {i < ITEMS.length - 1 && (
                  <span className="hidden lg:inline text-slate-700 ml-2 select-none" aria-hidden>·</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Compact mobile trust badge row — used inside hero sections & tight layouts. */
export function TrustStripMobile() {
  return (
    <div className="sm:hidden bg-[#050B14] text-white overflow-x-auto no-scrollbar" data-testid="trust-strip-mobile">
      <div className="flex items-center gap-3 px-4 py-2">
        {ITEMS.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="flex items-center gap-1.5 shrink-0">
              <Icon className="w-3 h-3 text-ehi-accent" strokeWidth={2.2} />
              <span className="text-[10px] font-bold font-mono-tab tabular-nums">{it.value}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">{it.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
